import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { shopApi } from '@wa/shared';
import type { ShopSettings, ShopSettingsInput } from '@wa/shared';
import { Card, Field, Notice, Switch, errorText, input, primary, secondary } from './ui';

interface Band { upTo: number | null; fee: number }

/**
 * The page's look, how customers reach the business, and how orders get to
 * them. Delivery bands are edited as rows and saved as the JSON the checkout
 * reads — one representation, validated on the server.
 */
export function SettingsPanel() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['shop', 'settings'], queryFn: shopApi.settings });
  const shop = settings.data?.settings ?? null;
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: (patch: ShopSettingsInput) => shopApi.saveSettings(patch),
    onSuccess: () => { setError(null); setSaved(true); setTimeout(() => setSaved(false), 1500); void queryClient.invalidateQueries({ queryKey: ['shop'] }); },
    onError: (e) => setError(errorText(e, 'Could not save.')),
  });

  if (!shop) return <Notice kind="muted">{settings.isLoading ? 'Loading…' : 'Create the shop on the Overview tab first.'}</Notice>;

  return (
    <div className="flex flex-col gap-4">
      {error && <Notice kind="bad">{error}</Notice>}
      {saved && <Notice kind="good">Saved.</Notice>}
      <LookCard shop={shop} presets={settings.data?.themePresets ?? []} busy={save.isPending} onSave={(p) => save.mutate(p)} />
      <ContactCard shop={shop} busy={save.isPending} onSave={(p) => save.mutate(p)} />
      <DeliveryCard shop={shop} busy={save.isPending} onSave={(p) => save.mutate(p)} />
      <BehaviourCard shop={shop} busy={save.isPending} onSave={(p) => save.mutate(p)} />
    </div>
  );
}

function useUpload(onDone: (url: string) => void) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true); setErr(null);
    try { onDone((await shopApi.uploadImage(file)).url); }
    catch (e) { setErr(errorText(e, 'Upload failed.')); }
    finally { setBusy(false); }
  };
  return { busy, err, pick };
}

function LookCard({ shop, presets, busy, onSave }: { shop: ShopSettings; presets: string[]; busy: boolean; onSave: (p: ShopSettingsInput) => void }) {
  const [description, setDescription] = useState(shop.description ?? '');
  const [color, setColor] = useState(shop.brandColor);
  const [theme, setTheme] = useState(shop.themePreset);
  const [logo, setLogo] = useState(shop.logoUrl ?? '');
  const [banner, setBanner] = useState(shop.bannerUrl ?? '');
  useEffect(() => { setDescription(shop.description ?? ''); setColor(shop.brandColor); setTheme(shop.themePreset); setLogo(shop.logoUrl ?? ''); setBanner(shop.bannerUrl ?? ''); }, [shop]);
  const logoUp = useUpload(setLogo);
  const bannerUp = useUpload(setBanner);

  return (
    <Card title="Look" blurb="Logo, colour and a short line under the name. The preset decides whether products are a grid of cards or a list.">
      <div className="grid gap-3 @min-[32rem]:grid-cols-2">
        <Field label="Short description"><input className={input} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={600} placeholder="Personalised gifts, delivered across India" /></Field>
        <Field label="Brand colour">
          <span className="flex items-center gap-2">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-8 w-12 rounded border border-(--color-line)" />
            <input className={input} value={color} onChange={(e) => setColor(e.target.value)} maxLength={7} />
          </span>
        </Field>
        <Field label="Layout">
          <select className={input} value={theme} onChange={(e) => setTheme(e.target.value)}>
            {presets.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Logo" hint="Square, up to 8 MB.">
          <span className="flex items-center gap-2">
            {logo && <img src={logo} alt="" className="size-10 rounded object-cover" />}
            <input type="file" accept="image/jpeg,image/png,image/webp" disabled={logoUp.busy} onChange={(e) => void logoUp.pick(e.target.files?.[0])} className="text-small" />
          </span>
        </Field>
        <Field label="Banner" hint="Wide, 1600×500 works well." className="@min-[32rem]:col-span-2">
          <span className="flex items-center gap-2">
            {banner && <img src={banner} alt="" className="h-10 w-32 rounded object-cover" />}
            <input type="file" accept="image/jpeg,image/png,image/webp" disabled={bannerUp.busy} onChange={(e) => void bannerUp.pick(e.target.files?.[0])} className="text-small" />
          </span>
        </Field>
      </div>
      {(logoUp.err || bannerUp.err) && <Notice kind="bad">{logoUp.err ?? bannerUp.err}</Notice>}
      <div><button type="button" className={primary} disabled={busy} onClick={() => onSave({ description, brandColor: color, themePreset: theme, logoUrl: logo, bannerUrl: banner })}>Save look</button></div>
    </Card>
  );
}

function ContactCard({ shop, busy, onSave }: { shop: ShopSettings; busy: boolean; onSave: (p: ShopSettingsInput) => void }) {
  const [phone, setPhone] = useState(shop.contactPhone ?? '');
  const [email, setEmail] = useState(shop.contactEmail ?? '');
  const [address, setAddress] = useState(shop.address ?? '');
  useEffect(() => { setPhone(shop.contactPhone ?? ''); setEmail(shop.contactEmail ?? ''); setAddress(shop.address ?? ''); }, [shop]);
  return (
    <Card title="Contact details" blurb="Shown at the foot of the page and on invoices.">
      <div className="grid gap-3 @min-[32rem]:grid-cols-2">
        <Field label="WhatsApp number" hint="Customers tap this to open a chat."><input className={input} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 88799 49772" /></Field>
        <Field label="Email"><input className={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Address" className="@min-[32rem]:col-span-2"><input className={input} value={address} onChange={(e) => setAddress(e.target.value)} maxLength={400} /></Field>
      </div>
      <div><button type="button" className={primary} disabled={busy} onClick={() => onSave({ contactPhone: phone, contactEmail: email, address })}>Save contact details</button></div>
    </Card>
  );
}

function DeliveryCard({ shop, busy, onSave }: { shop: ShopSettings; busy: boolean; onSave: (p: ShopSettingsInput) => void }) {
  const parse = (json: string | null): Band[] => {
    if (!json) return [{ upTo: null, fee: 0 }];
    try { const v = JSON.parse(json) as Band[]; return Array.isArray(v) && v.length ? v : [{ upTo: null, fee: 0 }]; } catch { return [{ upTo: null, fee: 0 }]; }
  };
  const [bands, setBands] = useState<Band[]>(parse(shop.deliveryRules));
  const [pickup, setPickup] = useState(shop.pickupEnabled);
  const [pickupNote, setPickupNote] = useState(shop.pickupNote ?? '');
  const [minOrder, setMinOrder] = useState(String(shop.minOrderDefault));
  useEffect(() => { setBands(parse(shop.deliveryRules)); setPickup(shop.pickupEnabled); setPickupNote(shop.pickupNote ?? ''); setMinOrder(String(shop.minOrderDefault)); }, [shop]);

  const set = (i: number, patch: Partial<Band>) => setBands((b) => b.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const addBand = () => setBands((b) => { const last = b[b.length - 1] ?? { upTo: null, fee: 0 }; return [...b.slice(0, -1), { upTo: (b[b.length - 2]?.upTo ?? 0) + 500, fee: 0 }, last]; });
  const removeBand = (i: number) => setBands((b) => b.filter((_, j) => j !== i));

  return (
    <Card title="Delivery and pickup" blurb="Delivery charge by order value — the last band has no upper limit. Pickup shows the customer your address and note instead of asking for theirs.">
      <div className="flex flex-col gap-2">
        {bands.map((b, i) => {
          const last = i === bands.length - 1;
          return (
            <div key={i} className="flex flex-wrap items-center gap-2 text-small">
              <span className="w-24 text-(--color-muted)">{last ? 'Above that' : i === 0 ? 'Orders up to' : 'Up to'}</span>
              {!last && <input className={`${input} w-28`} type="number" min={1} value={b.upTo ?? ''} onChange={(e) => set(i, { upTo: Number(e.target.value) })} />}
              <span className="text-(--color-muted)">charge ₹</span>
              <input className={`${input} w-24`} type="number" min={0} value={b.fee} onChange={(e) => set(i, { fee: Number(e.target.value) })} />
              {!last && <button type="button" className={secondary} onClick={() => removeBand(i)}>Remove</button>}
            </div>
          );
        })}
        <div><button type="button" className={secondary} onClick={addBand}>Add a band</button></div>
      </div>
      <div className="grid gap-3 @min-[32rem]:grid-cols-2">
        <Field label="Minimum order (₹)" hint="For customer types with no minimum of their own."><input className={input} type="number" min={0} value={minOrder} onChange={(e) => setMinOrder(e.target.value)} /></Field>
        <Field label="Pickup note" hint="Hours, landmark, what to bring."><input className={input} value={pickupNote} onChange={(e) => setPickupNote(e.target.value)} maxLength={300} /></Field>
      </div>
      <Switch checked={pickup} onChange={setPickup} label="Offer pickup" />
      <div><button type="button" className={primary} disabled={busy}
                   onClick={() => onSave({ deliveryRules: JSON.stringify(bands.map((b, i) => ({ upTo: i === bands.length - 1 ? null : b.upTo, fee: b.fee }))), pickupEnabled: pickup, pickupNote, minOrderDefault: Number(minOrder) || 0 })}>
        Save delivery
      </button></div>
    </Card>
  );
}

function BehaviourCard({ shop, busy, onSave }: { shop: ShopSettings; busy: boolean; onSave: (p: ShopSettingsInput) => void }) {
  const [hide, setHide] = useState(shop.hidePricesUntilSignin);
  const [incl, setIncl] = useState(shop.pricesIncludeTax);
  const [paused, setPaused] = useState(shop.orderingPaused);
  const [pauseMessage, setPauseMessage] = useState(shop.pauseMessage ?? '');
  useEffect(() => { setHide(shop.hidePricesUntilSignin); setIncl(shop.pricesIncludeTax); setPaused(shop.orderingPaused); setPauseMessage(shop.pauseMessage ?? ''); }, [shop]);
  return (
    <Card title="Prices and pauses">
      <Switch checked={hide} onChange={setHide} label="Show prices only after sign-in" hint="For wholesale: a visitor without a link from you sees products but no prices until they confirm their WhatsApp number." />
      <Switch checked={incl} onChange={setIncl} label="Prices include GST" hint="Off means tax is added at checkout and shown separately." />
      <Switch checked={paused} onChange={setPaused} label="Pause ordering" hint="The page stays up; Add to cart is replaced by your message." />
      {paused && <Field label="Pause message"><input className={input} value={pauseMessage} onChange={(e) => setPauseMessage(e.target.value)} maxLength={300} placeholder="Closed for Diwali — back on 25 October." /></Field>}
      <div><button type="button" className={primary} disabled={busy} onClick={() => onSave({ hidePricesUntilSignin: hide, pricesIncludeTax: incl, orderingPaused: paused, pauseMessage })}>Save</button></div>
    </Card>
  );
}
